/* jshint esversion: 6, -W097 */
/* globals require, module */
'use strict';

/**
 * Search related services, mainly logic that gets used in the
 * autocompleteDirective for app and cars.
 *
 * @module enervee_utils.search
 */

SearchService.$inject = [
    '$q',
    '$window',
    'CategoriesService',
    'FiltersService',
    'ModalQueueService',
    'SwiftypeService',
    'UtilService',
];
function SearchService(
    $q,
    $window,
    CategoriesService,
    FiltersService,
    ModalQueueService,
    SwiftypeService,
    UtilService
) {
    const _pub = {
        getSearchResults,
        performAutocompleteSearch,
        openAutocomplete,
        closeAutocomplete,
        toggleAutocomplete,
        isAutocompleteOpen,
        getCurrentFacetsAndRecords,
        getFacetsAndRecords,
        getInitialFacets,
        getNumberOfCategoryColumns,
        getCategoriesForColumns,
        getActiveCategoryIcons,
    };
    let _searchResults = null;
    let _isAutocompleteOpen = false;
    let _swiftypeFacetsAndRecords = null;

    /**
     * Gets the list of results
     *
     * @returns {*}
     */
    function getSearchResults() {
        return _searchResults;
    }

    /**
     * Performs a(n autocomplete) search for the given search term in the categoryless search box
     *
     * @param {string} searchTerm
     * @param {Promise} cancellerPromise A promise that, if rejected, cancels the search AJAX request
     * @param {Boolean} hasRebate Whether or not the search should only look for products with rebates
     */
    function performAutocompleteSearch(searchTerm, cancellerPromise, hasRebate) {
        return SwiftypeService.performAutocompleteSearch(searchTerm, {
            cancellerPromise: cancellerPromise,
            eligibleForRebatesOnly: hasRebate,
            applyDefaultFilters: true,
        }).then((records) => {
            _searchResults = _organizeAutocompleteResults(records);
        }, (response) => {
            return $q.reject(response);
        });
    }

    /**
     * Opens the autocomplete overlay
     */
    function openAutocomplete() {
        ModalQueueService.closePastModals(_pub.closeAutocomplete);
        _isAutocompleteOpen = true;
        angular.element('body').addClass('full-screen-open');
        angular.element('#call-to-action').removeClass('open');
        angular.element('.comparison-tray').removeClass('open');
    }

    /**
     * Closes the autocomplete overlay
     */
    function closeAutocomplete() {
        _isAutocompleteOpen = false;
        angular.element('body').removeClass('full-screen-open');
        angular.element('#call-to-action').addClass('open');
        angular.element('.comparison-tray').addClass('open');
    }

    /**
     * Toggles the autocomplete overlay
     */
    function toggleAutocomplete() {
        if (isAutocompleteOpen()) {
            closeAutocomplete();
        } else {
            openAutocomplete();
        }
    }

    /**
     * Returns whether or not the autocomplete overlay is open
     *
     * @returns {Boolean}
     */
    function isAutocompleteOpen() {
        return _isAutocompleteOpen;
    }

    /**
     * Gets and saves facets and records from swiftype based on category_code and options.
     *
     * @param {Number} categoryCode
     * @param {Object} [options={}]
     * @returns {*}
     */
    function getFacetsAndRecords(categoryCode, options={}) {
        const swiftypeFilters = UtilService.getSiteSetting('default_swiftype_filters', {});
        if (!options.filters[categoryCode] && swiftypeFilters[categoryCode]) {
            options.filters[categoryCode] = angular.extend({}, swiftypeFilters[categoryCode]);
        }

        return SwiftypeService.getResults(options).then(function (data) {
            _swiftypeFacetsAndRecords = {
                facets: data.info[categoryCode].facets,
                records: data.records[categoryCode]
            };
            return _swiftypeFacetsAndRecords;
        });
    }

    /**
     * Gets facets and records from last swiftype request.
     *
     * @returns {*}
     */
    function getCurrentFacetsAndRecords() {
        return _swiftypeFacetsAndRecords;
    }

    /**
     * Returns all Swiftype facets for the requested doctype
     *
     * @param  {string} documentType Doctype to get facets for
     * @returns {Promise<Object>}
     */
    function getInitialFacets(documentType) {
        let swiftypeOptions = {
            filters: {},
            document_types: [documentType],
        };
        return CategoriesService.getCategories().then(() => {
            return CategoriesService.getCategoryByCode(documentType);
        }).then((category) => {
            if (!category) return $q.reject(`"${documentType}" is not a valid category code!`);
            return FiltersService.getInitialSwiftypeFacets(documentType).then(facetsInfo => facetsInfo.facets.all);
        });
    }

    /**
     * Fetches the category object for passing to the category columns
     *
     * @returns {Promise<Object>}
     */
    function getCategoriesForColumns() {
        return CategoriesService.afterInitialCategoriesLoads(() => {
            /** 
             * Wait for the initial categories to load to make sure we
             * know which categories have Checkout.
             */
            return CategoriesService.getCategories(UtilitySelectorCookieService.getUtilityId());
        }).then(() => CategoriesService.getCategoryObject());
    };

    /**
     * Checks the current categories to see which icons from the `$scope.autocompleteIconConfig`
     * are currently being used. Returns a new array with the active `iconConfig`s.
     *
     * @param {Array<Object>} iconConfigList A list of config objects for the different
     * category icons
     * @param {Function} iconConfigList[].showIf A function that consumes a category and returns
     * a boolean if the category should show the corresponding icon
     * @returns {Array<Object>}
     */
    function getActiveCategoryIcons(iconConfigList) {
        const categories = CategoriesService.getOrderedCategories();
        if (!categories) return;
        const activeIcons = iconConfigList.filter((iconConfig) => {
            return categories.some(category => iconConfig.showIf(category));
        });
        /** 
         * We reverse the list because the icons on each category are floated
         * right, so they look like the order is backwards.
         */
        activeIcons.reverse();
        return activeIcons;
    }

    /**
     * Takes the results object from swiftype and organizes them. First, we remove any
     * categories that do not have any products, then sort them in an array by number
     * of products.
     *
     * @param {Object} results A map of categories to arrays of resulting products
     * @returns {Array<Object>}
     */
    function _organizeAutocompleteResults(results) {
        var resultsArray = [];
        angular.forEach(results, (products) => {
            if (products.length !== 0) {
                var category = CategoriesService.getCategoryById(products[0].category_id);
                resultsArray.push({category: category, products: products});
            }
        });
        resultsArray.sort((a, b) => {
            return b.products[0]._score - a.products[0]._score;
        });
        return resultsArray;
    }

    /**
     * Measures the innerWidth of the window and returns a number
     * of columns accordingly, used for the autocomplete columns
     * prior to a search being made.
     * Return values:
     * 0-500   -> 1
     * 501-768 -> 2
     * 769+    -> 3
     * 
     * @returns {Number}
     */
    function getNumberOfCategoryColumns() {
        const windowWidth = $window.innerWidth;
        if (windowWidth <= 500) {
            return 1;
        } else if (windowWidth <= 768) {
            return 2;
        }
        return 3;
    }

    return _pub;
}

module.exports = SearchService;